"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  NOTIFICATION_CATEGORY_LABELS,
  api,
  loadTokens,
  type NotificationCategory,
  type NotificationPrefs,
} from "@/lib/api";
import { useDisplayLocale } from "@/lib/displayLocale";
import { Skeleton } from "@/components/Skeleton";

const CATEGORIES: NotificationCategory[] = [
  "trade_fill",
  "market_resolution",
  "withdrawal_status",
  "social_interaction",
];

/**
 * EPIC-021 STORY-376 — notification preferences component.
 *
 * Optimistic toggle: flip the local row immediately on click, fire
 * the PATCH, roll back on error. Each toggle is a single PATCH (not
 * a "save all" button) so the UX matches what the user expects from
 * a settings page in 2026.
 *
 * Push column is rendered but disabled with a tooltip — push infra
 * lands when the mobile app ships (EPIC-009). Until then no toggle
 * does anything meaningful, so we surface that explicitly.
 */
export function NotificationSettings() {
  const { t } = useTranslation();
  // useDisplayLocale is still needed for the per-category labels
  // (NOTIFICATION_CATEGORY_LABELS is keyed by locale, not by i18n
  // key — those labels live in the API client today).
  const locale = useDisplayLocale();
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingKey, setPendingKey] = useState<string | null>(null);

  useEffect(() => {
    if (!loadTokens()) return;
    let cancelled = false;
    void api
      .getNotificationPrefs()
      .then((p) => {
        if (!cancelled) setPrefs(p);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : t("notificationSettings.loadFailed"),
        );
      });
    return () => {
      cancelled = true;
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleToggle(
    category: NotificationCategory,
    channel: "email" | "push",
    nextValue: boolean,
  ) {
    if (!prefs) return;
    const key = `${category}.${channel}`;
    if (pendingKey) return;

    const optimistic: NotificationPrefs = {
      ...prefs,
      [category]: { ...prefs[category], [channel]: nextValue },
    };
    setPrefs(optimistic);
    setPendingKey(key);
    setError(null);

    try {
      const updated = await api.updateNotificationPrefs({
        [category]: { [channel]: nextValue },
      });
      setPrefs(updated);
    } catch (err) {
      // Rollback to the prior state.
      setPrefs(prefs);
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("notificationSettings.saveFailed");
      setError(msg);
    } finally {
      setPendingKey(null);
    }
  }

  if (!prefs && !error) {
    return (
      <div
        role="status"
        aria-label={t("notificationSettings.loadingAria")}
        className="space-y-2 p-4"
      >
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
        <Skeleton className="h-10 w-full rounded-md" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <p
          role="alert"
          className="mb-3 px-3 text-[12px]"
          style={{ color: "var(--color-no)" }}
        >
          {error}
        </p>
      )}

      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-[var(--color-border)] text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
            <th className="px-4 py-3 text-left font-bold">
              {t("notificationSettings.category")}
            </th>
            <th className="px-4 py-3 text-center font-bold">
              {t("notificationSettings.email")}
            </th>
            <th className="px-4 py-3 text-center font-bold">
              {t("notificationSettings.push")}
              <span
                className="ml-1 rounded-[3px] border px-1 py-[1px] text-[8px] font-bold uppercase tracking-[0.4px]"
                style={{
                  borderColor: "var(--color-text3)",
                  color: "var(--color-text3)",
                }}
              >
                {t("notificationSettings.soonBadge")}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {prefs &&
            CATEGORIES.map((category) => {
              const labels = NOTIFICATION_CATEGORY_LABELS[category];
              const channels = prefs[category];
              return (
                <tr
                  key={category}
                  className="border-b border-[var(--color-border)] last:border-0"
                >
                  <td className="px-4 py-3">
                    <div className="font-bold text-[var(--color-text)]">
                      {labels[locale]}
                    </div>
                    <div className="mt-0.5 text-[11px] text-[var(--color-text3)]">
                      {labels.description[locale]}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      checked={channels.email}
                      pending={pendingKey === `${category}.email`}
                      onChange={(v) => handleToggle(category, "email", v)}
                      disabled={pendingKey !== null}
                      ariaLabel={t("notificationSettings.emailToggleAria", {
                        label: labels[locale],
                      })}
                    />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Toggle
                      checked={channels.push}
                      pending={pendingKey === `${category}.push`}
                      onChange={(v) => handleToggle(category, "push", v)}
                      disabled={true}
                      ariaLabel={t("notificationSettings.pushToggleAria", {
                        label: labels[locale],
                      })}
                    />
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}

function Toggle({
  checked,
  pending,
  disabled,
  onChange,
  ariaLabel,
}: {
  checked: boolean;
  pending: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex h-6 w-11 items-center rounded-full border transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
      style={{
        borderColor: checked
          ? "var(--color-brand)"
          : "var(--color-border)",
        background: checked
          ? "var(--color-brand)"
          : "var(--color-bg)",
      }}
    >
      <span
        className="inline-block h-4 w-4 rounded-full bg-white transition-transform"
        style={{
          transform: checked ? "translateX(22px)" : "translateX(4px)",
          opacity: pending ? 0.5 : 1,
        }}
      />
    </button>
  );
}
